// ═══════════════════════════════════════════════════════════════
// BATCH REPORT
// Path: src/components/gifts/BatchReport.jsx
//
// Printable batch audit report.
// Page 1: Summary — batch info, type breakdown, totals
// Page 2+: Line item detail
//
// Props:
//   batch       — gift_batches row
//   gifts       — array from batchService.getBatchGifts()
//   chapterName — chapter name for header
//   onClose     — () => void
//
// Blueprint: CMM_Batch_Gift_Entry_Blueprint_v1_0.md — Phase 5
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { Printer, X, FileText } from "lucide-react";
import { FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { PAYMENT_METHODS } from "../../constants/giftTypes";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getTypeLabel = (method) =>
  PAYMENT_METHODS.find((m) => m.id === method)?.label || method || "Other";

export const BatchReport = ({ batch, gifts, chapterName, onClose }) => {
  const totalAmount = useMemo(
    () => gifts.reduce((sum, g) => sum + (g.amount || 0), 0),
    [gifts]
  );

  const byType = useMemo(() => {
    const map = {};
    gifts.forEach((g) => {
      const key = g.paymentMethod || "other";
      const label = getTypeLabel(key);
      if (!map[key]) map[key] = { label, count: 0, total: 0 };
      map[key].count++;
      map[key].total += g.amount || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [gifts]);

  const byFund = useMemo(() => {
    const map = {};
    gifts.forEach((g) => {
      const key = g.fundName || "Undesignated";
      if (!map[key]) map[key] = { name: key, count: 0, total: 0 };
      map[key].count++;
      map[key].total += g.amount || 0;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [gifts]);

  // ── Styles ──────────────────────────────────────────────────
  const page = { fontFamily: FONT_FAMILY, color: "#1a1a1a", lineHeight: 1.5 };
  const hr = { border: "none", borderTop: "1px solid #ccc", margin: "16px 0" };
  const sectionTitle = {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#333",
    marginBottom: 6,
  };
  const th = {
    textAlign: "left",
    padding: "5px 8px",
    borderBottom: "2px solid #333",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#555",
  };
  const td = {
    padding: "4px 8px",
    borderBottom: "1px solid #e5e5e5",
    fontSize: 11,
  };
  const summaryRow = {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: 12,
    borderBottom: "1px solid #eee",
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .batch-report-overlay, .batch-report-overlay * { visibility: visible !important; }
          .batch-report-overlay {
            position: absolute !important;
            inset: 0 !important;
            background: white !important;
            padding: 0 !important;
            display: block !important;
          }
          .batch-report-overlay .no-print { display: none !important; }
          .batch-report-overlay .report-scroll {
            position: static !important;
            max-width: none !important;
            max-height: none !important;
            width: 100% !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
          }
          .page-break { page-break-before: always; }
          @page { margin: 0.6in 0.75in; }
        }
      `}</style>

      <div
        className="batch-report-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1100,
          padding: 16,
        }}
        onClick={onClose}
      >
        <div
          className="report-scroll"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 10,
            width: "100%",
            maxWidth: 800,
            maxHeight: "92vh",
            overflow: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {/* Toolbar */}
          <div
            className="no-print"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 20px",
              borderBottom: "1px solid #e5e5e5",
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
              }}
            >
              <FileText size={ICON_SIZES.sm} />
              Batch Audit Report
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => window.print()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                }}
              >
                <Printer size={ICON_SIZES.xs} />
                Print
              </button>
              <button
                onClick={onClose}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "5px 8px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                <X size={ICON_SIZES.sm} />
              </button>
            </div>
          </div>

          {/* ════════════════════════════════════════════════════ */}
          {/* PAGE 1: SUMMARY                                     */}
          {/* ════════════════════════════════════════════════════ */}
          <div style={{ padding: "28px 36px", ...page }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              {chapterName && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                  {chapterName}
                </div>
              )}
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>
                GIFT BATCH AUDIT REPORT
              </div>
            </div>

            {/* Batch info grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px 24px",
                padding: "14px 18px",
                background: "#f8f9fa",
                borderRadius: 6,
                border: "1px solid #e5e5e5",
                fontSize: 12,
                marginBottom: 20,
              }}
            >
              <div>
                <strong>Batch ID:</strong> {batch.batch_number}
              </div>
              <div>
                <strong>Batch Date:</strong> {fmtDate(batch.batch_date)}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                {batch.status === "closed" ? "Closed" : "Open"}
              </div>
              <div>
                <strong>Created:</strong>{" "}
                {new Date(batch.created_at).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </div>
              {batch.notes && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Notes:</strong> {batch.notes}
                </div>
              )}
            </div>

            {/* Gift Types Summary */}
            <div style={sectionTitle}>Gift Types in this Batch</div>
            <div
              style={{
                border: "1px solid #e5e5e5",
                borderRadius: 6,
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              {byType.map((t, idx) => (
                <div
                  key={t.label}
                  style={{
                    ...summaryRow,
                    padding: "8px 16px",
                    borderBottom:
                      idx < byType.length - 1 ? "1px solid #eee" : "none",
                    background: idx % 2 === 0 ? "#fafafa" : "#fff",
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{t.label}</span>
                  <span>
                    {t.count} gift{t.count !== 1 ? "s" : ""}
                    &nbsp;&nbsp;&nbsp;
                    <strong>{fmtCurrency(t.total)}</strong>
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 16px",
                  background: "#f0f0f0",
                  fontWeight: 700,
                  fontSize: 13,
                  borderTop: "2px solid #333",
                }}
              >
                <span>TOTAL</span>
                <span>
                  {gifts.length} gift{gifts.length !== 1 ? "s" : ""}
                  &nbsp;&nbsp;&nbsp;
                  {fmtCurrency(totalAmount)}
                </span>
              </div>
            </div>

            {/* Fund Summary */}
            {byFund.length > 0 && (
              <>
                <div style={sectionTitle}>Summary by Fund</div>
                <div
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 6,
                    overflow: "hidden",
                    marginBottom: 20,
                  }}
                >
                  {byFund.map((f, idx) => (
                    <div
                      key={f.name}
                      style={{
                        ...summaryRow,
                        padding: "8px 16px",
                        borderBottom:
                          idx < byFund.length - 1
                            ? "1px solid #eee"
                            : "none",
                        background: idx % 2 === 0 ? "#fafafa" : "#fff",
                      }}
                    >
                      <span>{f.name}</span>
                      <span>
                        {f.count} gift{f.count !== 1 ? "s" : ""}
                        &nbsp;&nbsp;&nbsp;
                        <strong>{fmtCurrency(f.total)}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Footer */}
            <div
              style={{
                fontSize: 9,
                color: "#999",
                textAlign: "center",
                marginTop: 24,
              }}
            >
              {batch.batch_number} · Page 1 — Summary
            </div>
          </div>

          {/* ════════════════════════════════════════════════════ */}
          {/* PAGE 2+: LINE ITEM DETAIL                           */}
          {/* ════════════════════════════════════════════════════ */}
          <div className="page-break" style={{ padding: "28px 36px", ...page }}>
            {/* Header repeated */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              {chapterName && (
                <div style={{ fontSize: 11, color: "#555" }}>{chapterName}</div>
              )}
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                {batch.batch_number} — Line Item Detail
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                {fmtDate(batch.batch_date)}
              </div>
            </div>

            {gifts.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 30,
                  color: "#999",
                  fontSize: 12,
                }}
              >
                No gifts in this batch.
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: 16,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...th, width: 24 }}>#</th>
                    <th style={th}>Donor</th>
                    <th style={{ ...th, textAlign: "right" }}>Amount</th>
                    <th style={th}>Type</th>
                    <th style={th}>Check #</th>
                    <th style={th}>Fund</th>
                    <th style={th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {gifts.map((gift, idx) => (
                    <tr key={gift.id}>
                      <td style={{ ...td, color: "#999" }}>{idx + 1}</td>
                      <td style={{ ...td, fontWeight: 600 }}>
                        {gift.memberName}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {fmtCurrency(gift.amount)}
                      </td>
                      <td style={td}>{getTypeLabel(gift.paymentMethod)}</td>
                      <td style={{ ...td, color: "#666" }}>
                        {gift.checkNumber || "—"}
                      </td>
                      <td style={td}>{gift.fundName || "—"}</td>
                      <td style={td}>{fmtDate(gift.giftDate)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: "2px solid #333",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    <td style={{ padding: "8px" }} />
                    <td style={{ padding: "8px" }}>
                      {gifts.length} gift{gifts.length !== 1 ? "s" : ""}
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                      }}
                    >
                      {fmtCurrency(totalAmount)}
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            )}

            {/* Footer */}
            <div
              style={{
                fontSize: 9,
                color: "#999",
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #ccc",
                paddingTop: 8,
                marginTop: 16,
              }}
            >
              <span>
                Printed:{" "}
                {new Date().toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
              <span>{batch.batch_number} — Line Items</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
